import { useEffect, useState } from 'react';
import type { Tariff } from '../../../../api/client';
import { getCachedTariffs, refreshTariffs } from '../../../../api/tariffsCache';
import { translateApiError } from '../../../lib/errors';

function gradient(t: Tariff): string {
  const stops = [...t.appearance.buttonGradient.stops]
    .sort((a, b) => a.value - b.value)
    .map((s) => `${s.color} ${s.value * 100}%`)
    .join(', ');

  return `linear-gradient(${t.appearance.buttonGradient.angle}deg, ${stops})`;
}

function formatPrice(amount: number | string): string {
  const value = Number(amount);

  if (Number.isNaN(value)) return String(amount);

  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** Количество песен в тарифе: GENERATION + GENERATION_VOCAL (TRACK не считаем). */
function getSongsCount(t: Tariff): number {
  return t.items.reduce(
    (sum, item) =>
      item.type === 'GENERATION' || item.type === 'GENERATION_VOCAL'
        ? sum + item.amount
        : sum,
    0,
  );
}

const SONG_FORMS: Record<string, string> = {
  one: 'песня',
  few: 'песни',
  many: 'песен',
  other: 'песен',
};
const ruPlural = new Intl.PluralRules('ru-RU');

/** «1 песня», «2 песни», «5 песен», «21 песня» */
function formatSongs(count: number): string {
  return `${count} ${SONG_FORMS[ruPlural.select(count)]}`;
}

interface Props {
  onPurchase: (tariffId: string) => Promise<void>;
}

const CARD_CLASS = 'rounded-2xl border-[1.5px] p-[18.5px]';
const PLACEHOLDER_CLASS =
  'rounded-2xl bg-gradient-to-r from-purple-50 to-pink-50 p-5 text-center text-sm text-gray-600';

export function TariffCards({ onPurchase }: Props) {
  const tariffParams = { order: 'ASC' as const, field: 'price' as const, limit: 50, offset: 0 };
  const cached = getCachedTariffs(tariffParams);
  const [items, setItems] = useState<Tariff[]>(() =>
    (cached?.tariffs ?? []).filter(
      (tariff) =>
        !tariff.items.some((item) => item.type === 'ADDITIONAL_GENERATION'),
    ),
  );
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState('');
  const [buying, setBuying] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    void refreshTariffs(tariffParams)
      .then((response) => {
        if (!mounted) return;
        setItems(
          response.tariffs.filter(
            (tariff) =>
              !tariff.items.some((item) => item.type === 'ADDITIONAL_GENERATION'),
          ),
        );
        setError('');
      })
      .catch((requestError) => {
        if (mounted && !cached) setError(translateApiError(requestError));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return <div className={PLACEHOLDER_CLASS}>Загружаем тарифы…</div>;
  }

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700"
      >
        {error}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className={PLACEHOLDER_CLASS}>Сейчас нет доступных тарифов.</div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((tariff) => {
        const songsCount = getSongsCount(tariff);

        return (
          <section
            key={tariff.id}
            className={CARD_CLASS}
            style={{
              background: tariff.appearance.background,
              borderColor: tariff.appearance.border,
            }}
            aria-labelledby={`tariff-title-${tariff.id}`}
          >
            <h3
              id={`tariff-title-${tariff.id}`}
              className="text-base leading-6 font-bold text-gray-900"
            >
              {tariff.name}
            </h3>

            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-3xl leading-9 font-bold tracking-tight text-gray-900">
                {formatPrice(tariff.price.amount)} ₽
              </span>
              {songsCount > 0 && (
                <span className="text-base leading-6 text-gray-500">
                  / {formatSongs(songsCount)}
                </span>
              )}
            </div>

            {tariff.shortDescription && (
              <p className="mt-1 text-sm leading-5 text-gray-500">
                {tariff.shortDescription}
              </p>
            )}

            <button
              type="button"
              disabled={buying === tariff.id}
              onClick={async () => {
                setBuying(tariff.id);
                try {
                  await onPurchase(tariff.id);
                } finally {
                  setBuying(null);
                }
              }}
              className="mt-4 w-full px-6 py-3 text-base leading-6 text-white rounded-xl font-semibold hover:shadow-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundImage: gradient(tariff) }}
            >
              {buying === tariff.id ? 'Переходим к оплате…' : 'Купить'}
            </button>
          </section>
        );
      })}
    </div>
  );
}
